/**
 * React-Native specific implementations of style patterns that
 * don't map directly to cross-platform design token values.
 */
import Platform from "react-native-web/dist/exports/Platform";
import { fontBasePixels } from '@telus-uds/system-constants';

// Make design tokens fit React Native's text styles' specific requirements and quirks
export function applyTextStyles(_ref) {
  let {
    fontColor,
    fontSize,
    fontScaleCap,
    lineHeight,
    letterSpacing,
    fontWeight = '400',
    fontName,
    fontStyle = 'normal',
    themeOptions = {
      // TODO: switch `forceAbsoluteFontSizing` to be false by default in the next major version
      forceAbsoluteFontSizing: true
    },
    ...rest
  } = _ref;
  const styles = {
    ...rest
  };
  const {
    forceAbsoluteFontSizing
  } = themeOptions;
  if (fontSize) {
    // If relative font sizes are needed, catch and calculate them here
    styles.fontSize = Platform.OS === 'web' && !forceAbsoluteFontSizing ? `${fontSize / fontBasePixels}rem` : fontSize;
  }
  if (typeof lineHeight === 'number') {
    // React Native expects absolute line heights but multipliers are better as design tokens
    styles.lineHeight = lineHeight * (fontSize || fontBasePixels);
  }
  if (typeof letterSpacing === 'number' && letterSpacing) {
    // Same as for line heights - React Native expects absolute letter spacing value
    styles.letterSpacing = letterSpacing * (fontSize || fontBasePixels);
  }
  if (fontName) {
    // Don't set undefined font families. May need some validation here that the font is available.
    // Android doesn't recognise font weights natively so apply custom font weights via `fontFamily`.
    styles.fontFamily = `${fontName}${fontWeight}${fontStyle}`;
  } else if (fontWeight) {
    // If using system default font, apply the font weight directly.
    // Font weight support in Android is limited to 'bold' or anything else === 'normal'.
    styles.fontWeight = Platform.OS === 'android' && Number(fontWeight) > 400 ? 'bold' : fontWeight;
  }
  if (fontColor) {
    styles.color = fontColor;
  }

  // TODO: look into calculating typographic spacing with PixelRatio.getFontScale()
  // Do while implementing advanced typography e.g. paragraph etc
  // https://github.com/telus/universal-design-system/issues/89

  return styles;
}

/**
 * React Native's in-built shadow support is limited. Take an object describing an ideal shadow and
 * return a platform-appropriate implementation that gets close to the described effect.
 *
 * @param {object}  shadowToken
 * @param {boolean} shadowToken.inset
 * @param {color}   shadowToken.string
 * @param {number}  shadowToken.offsetX
 * @param {number}  shadowToken.offsetY
 * @param {number}  shadowToken.blur
 * @param {number}  shadowToken.spread
 * @return {object}
 */
export function applyShadowToken(shadowToken) {
  if (!shadowToken) return {};
  const applyShadow = Platform.select({
    web: () => applyWebShadow(shadowToken),
    ios: () => applyIosShadow(shadowToken),
    android: () => applyAndroidShadow(shadowToken)
  });
  return applyShadow();
}
function applyWebShadow(_ref2) {
  let {
    inset,
    color = '',
    offsetX = 0,
    offsetY = 0,
    blur = 0,
    spread = 0
  } = _ref2;
  const insetString = inset ? 'inset ' : '';
  const boxShadow = {
    boxShadow: `${insetString}${offsetX}px ${offsetY}px ${blur}px ${spread}px ${color}`
  };
  return boxShadow;
}
function applyAndroidShadow(shadowToken) {
  const {
    color,
    inset,
    spread,
    offsetY,
    offsetX
  } = shadowToken;
  // Android shadows are very limited and workarounds are hacky. Use the built-in `elevation` API unless
  // shadow design is radically different to a simple top-down shadow (e.g. horizontal, upward, or inset)
  if (!inset && offsetY >= 0 && offsetY >= offsetX) {
    return {
      // Can't match other platforms, but can give longer shadows to elements designed for longer shadows
      elevation: spread + offsetY,
      shadowColor: color // Only applies on Android >= 9 and React Native >= 0.64.0
    };
  }
  // TODO: maybe use a library or workaround to increase capabilities.
  // https://github.com/telus/universal-design-system/issues/535
  return {};
}
function applyIosShadow(_ref3) {
  let {
    inset,
    color,
    offsetX,
    offsetY,
    spread,
    blur
  } = _ref3;
  // React Native iOS doesn't support inset shadows.
  // TODO: maybe use a library or workaround for inset shadows. This is a sizable task and would
  // involve changing component implementations to use a custom view, can't be done with styles alone.
  // https://github.com/telus/universal-design-system/issues/535
  if (inset) return {};

  // React Native iOS doesn't support spread. Where there's an offset, can approximate positive spread by
  // increasing the (positive or negative) offset by the spread. This makes the shadow reach the point it
  // would if spread was supported (with a side effect that its start is further "under" the element).
  const nudgeBySpread = offset => {
    if (!offset || spread <= 0) return 0;
    return offset > 0 ? spread : spread * -1;
  };
  return {
    shadowColor: color,
    shadowOffset: {
      width: offsetX + nudgeBySpread(offsetX),
      height: offsetY + nudgeBySpread(offsetY)
    },
    shadowRadius: blur,
    shadowOpacity: 1 // opacity is applied via RGBA in the `color` token
  };
}
const verticalAlignToFlexRowAlign = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end'
};
export function verticalAlignRow(verticalAlign) {
  let reverse = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return {
    flexDirection: reverse ? 'row-reverse' : 'row',
    alignItems: verticalAlignToFlexRowAlign[verticalAlign]
  };
}
const calculateBorderRadius = (borderRadius, outerBorderGap, outerBorderWidth) => {
  if (borderRadius) {
    return borderRadius + outerBorderGap + outerBorderWidth;
  }
  return null;
};
/**
 * Use on an outer container to create an outer border with an optional gap around it
 * that matches the border radius of any inner border.
 */
export const applyOuterBorder = _ref4 => {
  let {
    outerBorderColor,
    outerBorderWidth = 0,
    outerBorderGap = 0,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius
  } = _ref4;
  return {
    margin: 0 - outerBorderWidth - outerBorderGap,
    padding: outerBorderGap,
    borderRadius: calculateBorderRadius(borderRadius, outerBorderGap, outerBorderWidth),
    borderTopLeftRadius: calculateBorderRadius(borderTopLeftRadius, outerBorderGap, outerBorderWidth),
    borderTopRightRadius: calculateBorderRadius(borderTopRightRadius, outerBorderGap, outerBorderWidth),
    borderBottomLeftRadius: calculateBorderRadius(borderBottomLeftRadius, outerBorderGap, outerBorderWidth),
    borderBottomRightRadius: calculateBorderRadius(borderBottomRightRadius, outerBorderGap, outerBorderWidth),
    borderWidth: outerBorderWidth,
    borderColor: outerBorderColor
  };
};